/**
 * What the GitHub callback page should do with the URL GitHub sent it to.
 *
 * `/github/verify` refuses any `code` that doesn't come back with a state this
 * session minted. The App requests OAuth during installation, so a first install
 * returns with a `code` straight away — and with no state at all if the install
 * started from somewhere that couldn't mint one (the old frontend's
 * `/github/install` link, a bookmark, GitHub's own "Install" button). Posting that
 * code is a guaranteed refusal, so it is treated like the install leg instead: the
 * code is dropped unspent and the OAuth leg runs again. The user has just
 * authorized the App, so GitHub sends them straight back with a fresh code and our
 * state, with no extra click.
 *
 * That relies on GitHub echoing our state on the OAuth return, which it always
 * does. If it somehow didn't, re-authorizing would loop forever, so a second
 * stateless code inside the guard window is shown as an error instead.
 */
export type GithubCallbackStep =
  | { kind: 'verify'; code: string; state: string }
  | { kind: 'authorize' }
  | { kind: 'loop' };

export const REAUTHORIZE_GUARD_KEY = '_gh_reauthorize_at';
export const REAUTHORIZE_GUARD_MS = 2 * 60 * 1000;

export function githubCallbackStep(
  params: URLSearchParams,
  lastReauthorizeAt: number | null,
  now: number,
): GithubCallbackStep {
  const code = params.get('code');
  const state = params.get('state');
  if (code && state) return { kind: 'verify', code, state };
  if (code && lastReauthorizeAt !== null && now - lastReauthorizeAt < REAUTHORIZE_GUARD_MS) {
    return { kind: 'loop' };
  }
  return { kind: 'authorize' };
}
